import { RefObject, useEffect } from 'react';
import { DraggableRegion } from '../types/draggable-region';

function roundRect(rect: DOMRect, scale = 1): DraggableRegion {
  return {
    x: Math.round(rect.left * scale),
    y: Math.round(rect.top * scale),
    width: Math.round(rect.width * scale),
    height: Math.round(rect.height * scale),
    draggable: true,
  };
}

function roundRectNoDrag(rect: DOMRect, scale = 1): DraggableRegion {
  return { ...roundRect(rect, scale), draggable: false };
}

/**
 * Hook that wires up WildTangent draggable regions for a title bar.
 * - barRef: the whole draggable bar
 * - excludeRefs: any sub-elements that should NOT drag (e.g. button clusters)
 * - scale: multiply CSS pixel coordinates by this value if CEF expects physical pixels.
 *   If dragging feels offset on hiDPI, try scale=window.devicePixelRatio.
 */
export function useDraggableRegions(
  barRef: RefObject<HTMLElement | null>,
  excludeRefs: RefObject<HTMLElement | null>[],
  scale = 1,
) {
  useEffect(() => {
    const wt = window.wildtangent;
    if (!barRef.current || !wt?.SetDraggableRegions) return;

    const update = () => {
      try {
        const bar = barRef.current!;
        const barRect = bar.getBoundingClientRect();
        const regions: DraggableRegion[] = [roundRect(barRect, scale)];

        // Add non-draggable overlays for interactive controls
        for (const ref of excludeRefs) {
          const el = ref.current;
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          regions.push(roundRectNoDrag(rect, scale));
        }

        wt.SetDraggableRegions(regions);
      } catch (err) {
        // Silently ignore to avoid breaking UI if native bridge hiccups
        console.warn('SetDraggableRegions failed', err);
      }
    };

    // Observe size/position changes
    const resizeObs = new ResizeObserver(update);
    resizeObs.observe(barRef.current);
    excludeRefs.forEach((r) => r.current && resizeObs.observe(r.current));

    // Update on scroll/resize to keep coordinates in sync with viewport
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    // Initial paint
    update();

    return () => {
      resizeObs.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [barRef, excludeRefs, scale]);
}
